import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { TableComponent } from '../../atoms/table/table.component';
import { TableColumn } from '../../atoms/table/table.model';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { RANKING_LINES_INTENSITY_TABLE_DEFAULT_COLUMNS } from '../../../constants/table.constants';
import { BadgeIndicatorComponent } from '../../molecules/badge-indicator/badge-indicator.component';
import { PhaseIntensityBarComponent } from '../../molecules/charts/rsabt/phase-intensity-bar.chart';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CapacityRankingLine } from '@data';

@Component({
  selector: 'ui-ranking-lines-intensity-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TableComponent,
    TranslocoDirective,
    BadgeIndicatorComponent,
    PhaseIntensityBarComponent,
  ],
  template: `
    <ng-container *transloco="let t">
      @if (dataSource().length && columns()!.length && columnTemplates()) {
        <ui-table
          [dataSource]="dataSource()"
          [columns]="columns()"
          [columnTemplates]="columnTemplates()"
          [showPagination]="showPagination()"
          [loading]="loading()"
          [scrollConfig]="scrollConfig()"
        />
      }

      <ng-template #lineTemplate let-data="row">
        <div class="flex flex-col">
          <span class="bold">{{ data.lineName }}</span>
          <span class="text-neutral-7">{{ data.lineId }}</span>
        </div>
      </ng-template>

      <ng-template #maxIntensityTemplate let-data="row">
        <ui-badge-indicator
          [value]="data.lineIntensity"
          [percentage]="data.lineIntensityPercentage"
          [nominal]="data.lineNominalIntensity"
          [units]="'A'"
          [tooltipLabel]="t('DISTRIBUTION_NETWORK.TABLE.NOMINAL')"
        />
      </ng-template>

      <ng-template #intensityPhaseTemplate let-data="row">
        <ui-phase-intensity-bar
          [lineIntensity]="
            data.lineIntensityPhaseR +
            data.lineIntensityPhaseS +
            data.lineIntensityPhaseT
          "
          [intensityPhaseR]="data.lineIntensityPhaseR"
          [intensityPhaseS]="data.lineIntensityPhaseS"
          [intensityPhaseT]="data.lineIntensityPhaseT"
        />
      </ng-template>
    </ng-container>
  `,
})
export class RankingLinesIntensityTableComponent {
  DEFAULT_COLUMNS: TableColumn[] =
    RANKING_LINES_INTENSITY_TABLE_DEFAULT_COLUMNS;

  protected readonly translocoService = inject(TranslocoService);
  protected readonly destroyRef = inject(DestroyRef);

  data = input<CapacityRankingLine[]>([]);
  loading = input<boolean>(false);
  showPagination = input<boolean>(false);

  columnTemplates = signal<{ [key: string]: TemplateRef<any> }>({});
  columns = signal<TableColumn[]>(this.DEFAULT_COLUMNS);

  scrollConfigDefault = signal<{ x?: string | null; y?: string | null }>({
    y: '200px',
  });
  scrollConfig = computed<{ x?: string | null; y?: string | null }>(() =>
    this.data()?.length ? this.scrollConfigDefault() : {},
  );
  dataSource = computed(() =>
    this.data().map((item) => {
      // try to use an explicit id if backend provides it
      const stableId =
        (item as any).id ??
        // fallback to a combination of stable fields (adjust fields to your model)
        `${item.tsabtId ?? ''}_${item.lineName ?? ''}_${item.order ?? 0}`;
      return { ...item, id: stableId };
    }),
  );

  lineTemplate = viewChild<TemplateRef<any>>('lineTemplate');
  maxIntensityTemplate = viewChild<TemplateRef<any>>('maxIntensityTemplate');
  intensityPhaseTemplate = viewChild<TemplateRef<any>>(
    'intensityPhaseTemplate',
  );

  e1 = effect(() => {
    const lineTemplate = this.lineTemplate();
    const maxIntensityTemplate = this.maxIntensityTemplate();
    const intensityPhaseTemplate = this.intensityPhaseTemplate();
    if (lineTemplate && maxIntensityTemplate && intensityPhaseTemplate) {
      this.columnTemplates.set({
        line: lineTemplate,
        maxIntensity: maxIntensityTemplate,
        intensityPhase: intensityPhaseTemplate,
      });
    }
  });

  constructor() {
    this.setLocalizeColumns();
  }

  setLocalizeColumns() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columns.set(
          this.DEFAULT_COLUMNS.map((col) => ({
            ...col,
            title: this.translocoService.translate(col.title),
          })),
        );
      });
  }
}
